import React, { useState, useMemo } from 'react';
import { useRestoMod } from '../../context/RestoModContext';
import {
  BOMItem,
  ComponentCondition,
  PartsInquiryFormat,
  PartsInquiryRequest,
} from '../../types/bom';
import { generatePartsInquiry } from '../../utils/hollanderEngine';
import { MechanicalButton } from '../common/MechanicalButton';
import { ConditionBadge } from '../common/ConditionBadge';
import {
  X,
  FileText,
  Mail,
  Share2,
  Copy,
  Check,
  ExternalLink,
  MessageSquare,
  Building2,
  ShieldCheck,
} from 'lucide-react';

export interface PartsInquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: BOMItem;
}

const ALL_CONDITIONS: ComponentCondition[] = [
  'OEM NOS',
  'Reproduction',
  'Salvage',
  'Remanufactured',
];

export const PartsInquiryModal: React.FC<PartsInquiryModalProps> = ({
  isOpen,
  onClose,
  item,
}) => {
  const { state } = useRestoMod();
  const { project } = state;

  const [format, setFormat] = useState<PartsInquiryFormat>('plain_text');
  const [vehicleYMM, setVehicleYMM] = useState(
    project?.title || `${project?.year || 1969} ${project?.make || 'Chevrolet'} ${project?.model || 'Camaro'}`
  );
  const [vin, setVin] = useState(project?.vin || '');
  const [acceptableConditions, setAcceptableConditions] = useState<ComponentCondition[]>([
    item.condition,
    'Salvage',
    'OEM NOS',
  ]);
  const [targetBudget, setTargetBudget] = useState<number>(item.estimatedCost || 0);
  const [shippingZip, setShippingZip] = useState('90210');
  const [contactName, setContactName] = useState('RestoMod Studio Lead Builder');
  const [contactEmailOrPhone, setContactEmailOrPhone] = useState('parts@restomodstudio.garage');
  const [additionalNotes, setAdditionalNotes] = useState(item.notes || '');

  const [copied, setCopied] = useState(false);

  // Toggle conditions in array
  const toggleCondition = (cond: ComponentCondition) => {
    setAcceptableConditions((prev) =>
      prev.includes(cond) ? prev.filter((c) => c !== cond) : [...prev, cond]
    );
  };

  const requestPayload: PartsInquiryRequest = useMemo(() => {
    return {
      vehicleYearMakeModel: vehicleYMM,
      vin: vin.trim() || undefined,
      bomItem: item,
      acceptableConditions:
        acceptableConditions.length > 0 ? acceptableConditions : [item.condition],
      targetBudget: targetBudget > 0 ? targetBudget : undefined,
      shippingZip: shippingZip.trim() || undefined,
      contactName: contactName.trim() || 'RestoMod Builder',
      contactEmailOrPhone: contactEmailOrPhone.trim() || 'parts@restomodstudio.garage',
      additionalNotes: additionalNotes.trim() || undefined,
    };
  }, [
    vehicleYMM,
    vin,
    item,
    acceptableConditions,
    targetBudget,
    shippingZip,
    contactName,
    contactEmailOrPhone,
    additionalNotes,
  ]);

  const outputText = useMemo(() => {
    return generatePartsInquiry(requestPayload, format);
  }, [requestPayload, format]);

  const handleCopy = () => {
    navigator.clipboard.writeText(outputText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 overflow-y-auto">
      <div className="bg-shop-panel border-2 border-shop-steel rounded shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden my-auto text-shop-light">
        {/* Modal Header */}
        <div className="px-5 py-3.5 bg-shop-darkest border-b border-shop-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-shop-panel rounded border border-shop-border text-safety-amber">
              <Share2 className="w-5 h-5" />
            </span>
            <div>
              <h3 className="text-base font-bold text-shop-highlight uppercase tracking-wider">
                Structured Parts Inquiry &amp; RFQ Generator
              </h3>
              <p className="text-xs text-shop-muted">
                Export standardized sourcing requests for salvage yards, car clubs, swap meets, and online forums
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded bg-shop-dark border border-shop-border text-shop-muted hover:text-shop-highlight hover:border-shop-steel cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 overflow-y-auto flex-1 space-y-6">
          {/* Target Part Header Summary */}
          <div className="p-3.5 bg-shop-darkest border border-shop-border rounded flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-shop-highlight uppercase">
                  {item.partName}
                </span>
                <ConditionBadge condition={item.condition} size="sm" />
              </div>
              <div className="text-xs font-mono text-shop-muted mt-1">
                OEM PN: <strong className="text-shop-light">{item.partNumber || 'N/A'}</strong> |
                Vendor: <strong className="text-shop-light">{item.vendor}</strong> |
                Hollander #: <strong className="text-safety-amber">{item.hollanderNumber || 'N/A'}</strong>
              </div>
            </div>

            <div className="text-right font-mono text-xs">
              <span className="text-shop-muted block">Budgeted Cost:</span>
              <span className="text-sm font-bold text-safety-amber">
                ${item.estimatedCost.toFixed(2)} (Qty: {item.quantity})
              </span>
            </div>
          </div>

          {/* 4 Output Format Selector Tabs */}
          <div className="space-y-2">
            <label className="text-xs font-mono font-bold text-safety-amber uppercase tracking-wider block">
              1. Choose Inquiry Target Format
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button
                type="button"
                onClick={() => setFormat('plain_text')}
                className={`min-h-[48px] px-3 py-2 rounded border text-xs font-mono font-bold flex items-center gap-2 transition-all cursor-pointer ${
                  format === 'plain_text'
                    ? 'bg-shop-darkest border-safety-amber text-shop-highlight shadow-[inset_0_2px_0_0_#f59e0b]'
                    : 'bg-shop-dark text-shop-muted border-shop-border hover:bg-shop-well'
                }`}
              >
                <FileText className="w-4 h-4 text-safety-amber shrink-0" />
                <span className="truncate">Plain Text / Clipboard</span>
              </button>

              <button
                type="button"
                onClick={() => setFormat('salvage_rfq')}
                className={`min-h-[48px] px-3 py-2 rounded border text-xs font-mono font-bold flex items-center gap-2 transition-all cursor-pointer ${
                  format === 'salvage_rfq'
                    ? 'bg-shop-darkest border-safety-amber text-shop-highlight shadow-[inset_0_2px_0_0_#f59e0b]'
                    : 'bg-shop-dark text-shop-muted border-shop-border hover:bg-shop-well'
                }`}
              >
                <Building2 className="w-4 h-4 text-blueprint-cyan shrink-0" />
                <span className="truncate">Salvage Yard RFQ</span>
              </button>

              <button
                type="button"
                onClick={() => setFormat('forum_bbcode')}
                className={`min-h-[48px] px-3 py-2 rounded border text-xs font-mono font-bold flex items-center gap-2 transition-all cursor-pointer ${
                  format === 'forum_bbcode'
                    ? 'bg-shop-darkest border-safety-amber text-shop-highlight shadow-[inset_0_2px_0_0_#f59e0b]'
                    : 'bg-shop-dark text-shop-muted border-shop-border hover:bg-shop-well'
                }`}
              >
                <MessageSquare className="w-4 h-4 text-safety-green shrink-0" />
                <span className="truncate">Forum BBCode [WTB]</span>
              </button>

              <button
                type="button"
                onClick={() => setFormat('mailto_url')}
                className={`min-h-[48px] px-3 py-2 rounded border text-xs font-mono font-bold flex items-center gap-2 transition-all cursor-pointer ${
                  format === 'mailto_url'
                    ? 'bg-shop-darkest border-safety-amber text-shop-highlight shadow-[inset_0_2px_0_0_#f59e0b]'
                    : 'bg-shop-dark text-shop-muted border-shop-border hover:bg-shop-well'
                }`}
              >
                <Mail className="w-4 h-4 text-[#ec4899] shrink-0" />
                <span className="truncate">Mailto Link</span>
              </button>
            </div>
          </div>

          {/* Sourcing Inquiry Options Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-shop-darkest p-4 rounded border border-shop-border">
            {/* Left Column: Vehicle & Conditions */}
            <div className="space-y-3">
              <h5 className="text-xs font-mono font-bold text-shop-muted uppercase">
                Vehicle &amp; Condition Criteria
              </h5>

              <div className="space-y-1">
                <label className="text-[11px] font-mono text-shop-muted uppercase">
                  Target Vehicle (Year / Make / Model)
                </label>
                <input
                  type="text"
                  value={vehicleYMM}
                  onChange={(e) => setVehicleYMM(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-1.5 bg-shop-panel border border-shop-border rounded text-xs font-mono text-shop-highlight focus:outline-none focus:border-safety-amber"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-mono text-shop-muted uppercase">
                  Chassis VIN # (Optional)
                </label>
                <input
                  type="text"
                  value={vin}
                  onChange={(e) => setVin(e.target.value)}
                  placeholder="e.g. 124379N501234"
                  className="w-full min-h-[44px] px-3 py-1.5 bg-shop-panel border border-shop-border rounded text-xs font-mono text-shop-highlight focus:outline-none focus:border-safety-amber"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-mono text-shop-muted uppercase block">
                  Acceptable Conditions (Multi-select)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_CONDITIONS.map((cond) => {
                    const isSelected = acceptableConditions.includes(cond);
                    return (
                      <button
                        type="button"
                        key={cond}
                        onClick={() => toggleCondition(cond)}
                        className={`min-h-[44px] px-2 py-1.5 rounded border text-xs font-mono text-left flex items-center justify-between cursor-pointer ${
                          isSelected
                            ? 'bg-shop-panel border-safety-amber text-shop-highlight'
                            : 'bg-shop-dark text-shop-muted border-shop-border opacity-60'
                        }`}
                      >
                        <ConditionBadge condition={cond} size="sm" />
                        {isSelected && <Check className="w-3.5 h-3.5 text-safety-amber" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Column: Contact & Budget */}
            <div className="space-y-3">
              <h5 className="text-xs font-mono font-bold text-shop-muted uppercase">
                Budget, Location &amp; Contact Info
              </h5>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[11px] font-mono text-shop-muted uppercase">
                    Target Budget ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="5"
                    value={targetBudget}
                    onChange={(e) => setTargetBudget(parseFloat(e.target.value) || 0)}
                    className="w-full min-h-[44px] px-3 py-1.5 bg-shop-panel border border-shop-border rounded text-xs font-mono text-safety-amber font-bold focus:outline-none focus:border-safety-amber"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-mono text-shop-muted uppercase">
                    Shipping Zip Code
                  </label>
                  <input
                    type="text"
                    value={shippingZip}
                    onChange={(e) => setShippingZip(e.target.value)}
                    placeholder="e.g. 90210"
                    className="w-full min-h-[44px] px-3 py-1.5 bg-shop-panel border border-shop-border rounded text-xs font-mono text-shop-light focus:outline-none focus:border-safety-amber"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-mono text-shop-muted uppercase">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-1.5 bg-shop-panel border border-shop-border rounded text-xs font-mono text-shop-light focus:outline-none focus:border-safety-amber"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-mono text-shop-muted uppercase">
                  Contact Email / Phone
                </label>
                <input
                  type="text"
                  value={contactEmailOrPhone}
                  onChange={(e) => setContactEmailOrPhone(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-1.5 bg-shop-panel border border-shop-border rounded text-xs font-mono text-shop-light focus:outline-none focus:border-safety-amber"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-mono text-shop-muted uppercase">
                  Special Instructions / Casting Details
                </label>
                <input
                  type="text"
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  placeholder="e.g. Must have 28-spline axles and intact backing plates"
                  className="w-full min-h-[44px] px-3 py-1.5 bg-shop-panel border border-shop-border rounded text-xs text-shop-light font-sans focus:outline-none focus:border-safety-amber"
                />
              </div>
            </div>
          </div>

          {/* Output Preview Box */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-mono font-bold text-safety-amber uppercase tracking-wider flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                Generated Output ({format.replace('_', ' ').toUpperCase()})
              </label>

              <div className="flex items-center gap-2">
                {format === 'mailto_url' && (
                  <a
                    href={outputText}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="min-h-[44px] px-3 py-1.5 rounded bg-shop-panel border border-shop-border text-xs font-mono text-blueprint-cyan hover:border-blueprint-cyan inline-flex items-center gap-1.5"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open Email Client
                  </a>
                )}

                <MechanicalButton
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleCopy}
                  icon={copied ? <Check className="w-4 h-4 text-shop-darkest" /> : <Copy className="w-4 h-4" />}
                >
                  {copied ? 'Copied to Clipboard!' : 'Copy to Clipboard'}
                </MechanicalButton>
              </div>
            </div>

            <pre className="p-4 bg-shop-darkest border border-shop-border rounded text-xs font-mono text-shop-light whitespace-pre-wrap leading-relaxed max-h-56 overflow-y-auto select-all selection:bg-safety-amber selection:text-shop-darkest">
              {outputText}
            </pre>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-5 py-3.5 bg-shop-darkest border-t border-shop-border flex items-center justify-between">
          <span className="text-xs font-mono text-shop-muted">
            Format ready for direct paste into classifieds, emails, or shop faxes.
          </span>
          <MechanicalButton variant="secondary" size="sm" onClick={onClose}>
            Done
          </MechanicalButton>
        </div>
      </div>
    </div>
  );
};
